import { useCallback, useEffect, useState } from 'react';
import { Loader2, RefreshCw } from 'lucide-react';
import { fmtUsd, fmtUsdSigned } from '@/components/cards/cardFormat';
import { apiFetch } from '@/stores/authStore';
import { useMarketStore } from '@/stores/marketStore';
import { useDisplaySettings } from '@/stores/displaySettings';

interface OpenPositionLite {
  ticker: string;
  side: 'yes' | 'no';
  netCount: number;
  avgEntryPriceCents: number;
  /** Exact basis in cents from the relay ledger; older relays omit it. */
  costBasisCents?: number;
}

export interface OrgTraderRow {
  userId: string;
  username: string;
  openCount: number;
  openNotionalCents: number;
  realizedPnlCents: number;
  openOrderCount?: number;
  openPositions?: OpenPositionLite[];
}

interface OrgTradersTableProps {
  orgQueryParam?: string;
  onRowClick?: (row: OrgTraderRow) => void;
  highlightUserId?: string;
  title?: string;
}

// Comma-grouping via the shared card formatter (five-figure org P&L).
function formatDollarsSigned(cents: number): string {
  return fmtUsdSigned(cents / 100);
}

export function OrgTradersTable({
  orgQueryParam = '',
  onRowClick,
  highlightUserId,
  title = 'Org Positions by Trader',
}: OrgTradersTableProps) {
  const [rows, setRows] = useState<OrgTraderRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Same masking the positions tiles honor (default hidden) — without this a
  // manager sees ••• tiles above a table of everyone's live P&L dollars.
  const showPnl = useDisplaySettings((s) => s.showPnl);

  // Re-derive Unrealized P&L when hydra ticks update prices.
  const marketVersion = useMarketStore((s) => s.version);
  const getMarketPrice = useMarketStore((s) => s.getMarketPrice);
  void marketVersion;

  const computeUnrealized = (row: OrgTraderRow): number | null => {
    const positions = row.openPositions;
    if (!positions || positions.length === 0) return 0;
    let total = 0;
    let hadAny = false;
    for (const p of positions) {
      const yesPrice = getMarketPrice(p.ticker);
      if (yesPrice == null) continue;
      hadAny = true;
      // Both sides use side-native prices: avg is what was paid for that
      // side's contract (raw, not YES-equivalent), current is side-native
      // (yes → yesPrice; no → 100 − yesPrice). Same formula either way.
      const current = p.side === 'yes' ? yesPrice : 100 - yesPrice;
      // Prefer the exact basis — re-multiplying the whole-cent-rounded avg
      // drifts up to 0.5¢ × netCount vs the trader's own P&L view (I-035).
      total += current * p.netCount - (p.costBasisCents ?? p.avgEntryPriceCents * p.netCount);
    }
    return hadAny ? total : null;
  };

  const fetchRows = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiFetch(`/api/manager/org/positions-by-trader${orgQueryParam}`);
      const data = await res.json();
      if (!res.ok) {
        setError(data?.error ?? 'Failed to load');
        setRows([]);
      } else {
        setRows(data.rows ?? []);
      }
    } catch {
      setError('Network error');
    }
    setLoading(false);
  }, [orgQueryParam]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  return (
    <div className="bg-card border-border mt-6 rounded-lg border">
      <div className="border-border flex items-center justify-between border-b px-4 py-2.5">
        <h2 className="text-foreground text-sm font-medium">{title}</h2>
        <button
          onClick={fetchRows}
          title="Refresh"
          className="text-muted-foreground hover:text-foreground hover:bg-muted flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px]"
        >
          <RefreshCw size={10} /> Refresh
        </button>
      </div>
      {loading ? (
        <div className="flex justify-center py-6">
          <Loader2 className="text-muted-foreground h-4 w-4 animate-spin" />
        </div>
      ) : error ? (
        <p className="text-muted-foreground px-4 py-4 text-xs">{error}</p>
      ) : rows.length === 0 ? (
        <p className="text-muted-foreground px-4 py-4 text-xs">No activity yet.</p>
      ) : (
        <table className="w-full text-xs">
          <thead>
            <tr className="text-muted-foreground border-border border-b text-[11px] font-medium uppercase tracking-wider">
              <th className="px-4 py-2 text-left">Member</th>
              <th className="px-3 py-2 text-right">Open Contracts</th>
              <th className="px-3 py-2 text-right">Open Orders</th>
              <th className="px-3 py-2 text-right">Open Notional</th>
              <th className="px-3 py-2 text-right">Unrealized</th>
              <th className="px-3 py-2 text-right">Realized P&L</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const isHighlight = r.userId === highlightUserId;
              const clickable = !!onRowClick;
              const unrealized = computeUnrealized(r);
              return (
                <tr
                  key={r.userId}
                  onClick={clickable ? () => onRowClick(r) : undefined}
                  className={`border-border/40 border-b ${
                    clickable ? 'hover:bg-muted/30 cursor-pointer' : ''
                  } ${isHighlight ? 'bg-muted/40' : ''}`}
                >
                  <td className="text-foreground px-4 py-2">
                    {r.username}
                    {isHighlight && (
                      <span className="text-muted-foreground ml-2 text-[10px]">(you)</span>
                    )}
                  </td>
                  <td className="text-foreground px-3 py-2 text-right font-mono">{r.openCount}</td>
                  <td className="text-foreground px-3 py-2 text-right font-mono">
                    {r.openOrderCount ?? 0}
                  </td>
                  <td className="text-foreground px-3 py-2 text-right font-mono">
                    {fmtUsd(r.openNotionalCents / 100)}
                  </td>
                  <td
                    className={`px-3 py-2 text-right font-mono font-medium ${
                      !showPnl || unrealized == null
                        ? 'text-muted-foreground'
                        : unrealized >= 0
                          ? 'text-green-500'
                          : 'text-red-500'
                    }`}
                  >
                    {unrealized == null ? '—' : showPnl ? formatDollarsSigned(unrealized) : '•••'}
                  </td>
                  <td
                    className={`px-3 py-2 text-right font-mono font-medium ${
                      !showPnl
                        ? 'text-muted-foreground'
                        : r.realizedPnlCents >= 0
                          ? 'text-green-500'
                          : 'text-red-500'
                    }`}
                  >
                    {showPnl ? formatDollarsSigned(r.realizedPnlCents) : '•••'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}
